import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import styles from "./Checkout.module.scss";
import { useCartCheckout, CartModalStage } from "./hooks/useCartCheckout";
import { PageHeader } from "../../common/components/pageHeader/PageHeader";
import { Button } from "../../common/components/button/Button";
import { DeliveryType } from "../../common/types/enums";
import { ValidationError } from "./components/ValidationError";
import { DeliveryTypeSelector } from "./components/DeliveryTypeSelector";
import { StoreSelector } from "./components/StoreSelector";
import { AddressCard } from "./components/AddressCard";
import { OrderDetailsSection } from "./components/OrderDetailsSection";
import { PromoSelector } from "./components/PromoSelector";
import { BonusInfo } from "./components/BonusInfo";
import { BonusSpendSelector } from "./components/BonusSpendSelector";
import { BonusEarnInfo } from "./components/BonusEarnInfo";
import { BonusMotivator } from "./components/BonusMotivator";
import { PaymentMethodSelector } from "./components/PaymentMethodSelector";
import { OrderSummary } from "./components/OrderSummary";
import { CdekSelector } from "./components/CdekSelector";
import { CutlerySelector } from "./components/CutlerySelector";
import { DeliveryTimeSelector } from "./components/DeliveryTimeSelector";
import { GiftSelector } from "./components/GiftSelector";
import { AddressForm } from "../profile/address/AddressForm";
import { ORDERS_ROUTE, PROFILE_ROUTE } from "../../common/utils/constants";

export const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clusterId = Number(searchParams.get("clusterId") || 0);
  const [addingAddress, setAddingAddress] = useState(false);

  const {
    cluster,
    stage,
    deliveryType,
    selectedAddressId,
    addresses,
    paymentMethod,
    orderNumber,
    orderDisabled,
    isOrderCreating,
    validationError,
    deliveryAvailable,
    deliveryError,
    deliveryTimeDisplay,
    deliveryTimePickerVisible,
    openDeliveryTimePicker,
    closeDeliveryTimePicker,
    onDeliveryTimeSelect,
    deliveryTimeMinimumDate,
    itemsForOrder,
    itemsRemaining,
    itemsTotal,
    deliveryFee,
    discountAmount,
    totalWithDelivery,
    selectedPromo,
    discountCode,
    applicablePromos,
    bonusBalance,
    bonusPercent,
    bonusesToSpend,
    maxWriteOff,
    earnedBonuses,
    nextLevelText,
    storeList,
    pickUpStore,
    phone,
    comment,
    getValidationResult,
    inputHandler,
    setDirty,
    MAX_COMMENT_LENGTH,
    createAddress,
    createOrder,
    handleDeliveryTypeChange,
    handleAddressSelect,
    handlePaymentMethodChange,
    handlePickUpStoreChange,
    handleSelectPromo,
    setDiscountCode,
    setBonusesToSpend,
    isTimeAvailable,
    cdekOptions,
    selectedTariff,
    selectedPickupPoint,
    setSelectedTariff,
    setSelectedPickupPoint,
    deliveryService,
    cutleryCount,
    incrementCutlery,
    decrementCutlery,
    saleItemList,
    selectedGiftItemId,
    setSelectedGiftItemId,
  } = useCartCheckout();

  if (!clusterId) return <Navigate to="/cart" replace />;

  if (stage === CartModalStage.ORDER_SUCCESS) {
    return (
      <div className={styles.Page}>
        <Helmet>
          <title>Заказ оформлен — ЛОГМОЛЛ</title>
        </Helmet>
        <PageHeader title="Заказ оформлен" backRoute={ORDERS_ROUTE} />
        <div className={styles.Success}>
          <p className={styles.SuccessText}>
            Заказ #{orderNumber ?? ""} успешно создан.
          </p>
          <p className={styles.SuccessSub}>
            Актуальный статус — в истории заказов.
          </p>
          <div className={styles.SuccessActions}>
            <Button
              title="К заказам"
              onClick={() => navigate(ORDERS_ROUTE)}
              variant="primary"
              fullWidth
            />
            <Button
              title="В профиль"
              onClick={() => navigate(PROFILE_ROUTE)}
              variant="secondary"
              fullWidth
            />
          </div>
        </div>
      </div>
    );
  }

  const bonusMode: "spend" | "earn" = bonusesToSpend > 0 ? "spend" : "earn";
  const canSpendBonuses = bonusBalance > 0 && maxWriteOff > 0;

  const giftItems = selectedPromo?.giftItemIds
    ? selectedPromo.giftItemIds
        .map((id) => saleItemList.find((it) => it.id === id))
        .filter((it): it is NonNullable<typeof it> => !!it)
    : [];

  const giftItemName = giftItems.find(
    (it) => it.id === selectedGiftItemId,
  )?.name;

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>Оформление заказа — ЛОГМОЛЛ</title>
      </Helmet>

      <PageHeader
        title="Оформление заказа"
        subtitle={cluster?.name}
        backRoute={`/cart?clusterId=${clusterId}`}
      />

      <div className={styles.Content}>
        <div className={styles.Section}>
          <DeliveryTypeSelector
            clusterId={clusterId}
            deliveryType={deliveryType}
            onSelect={handleDeliveryTypeChange}
          />
        </div>

        {deliveryType === DeliveryType.DELIVERY && (
          <div className={styles.Section}>
            <h3 className={styles.SectionTitle}>Адрес доставки</h3>

            {addingAddress ? (
              <AddressForm
                initialData={{} as any}
                onCancel={() => setAddingAddress(false)}
                onSubmit={async (data) => {
                  await createAddress(data);
                  setAddingAddress(false);
                }}
              />
            ) : (
              <Button
                title="Добавить адрес"
                onClick={() => setAddingAddress(true)}
                variant="secondary"
                fullWidth
              />
            )}

            <div className={styles.Addresses}>
              {addresses.map((address) => (
                <AddressCard
                  key={address.id}
                  address={address}
                  isSelected={selectedAddressId === address.id}
                  onSelect={handleAddressSelect}
                />
              ))}
            </div>
          </div>
        )}

        {deliveryType === DeliveryType.PICKUP && storeList.length > 0 && (
          <div className={styles.Section}>
            <StoreSelector
              stores={storeList}
              pickUpStore={pickUpStore}
              onChange={handlePickUpStoreChange}
            />
          </div>
        )}

        {cdekOptions && (
          <div className={styles.Section}>
            <CdekSelector
              tariffs={cdekOptions.tariffs}
              pickupPoints={cdekOptions.pickupPoints}
              selectedTariff={selectedTariff}
              selectedPickupPoint={selectedPickupPoint}
              onSelectTariff={setSelectedTariff}
              onSelectPickupPoint={setSelectedPickupPoint}
            />
          </div>
        )}

        <div className={styles.Section}>
          <OrderDetailsSection
            phone={phone}
            comment={comment}
            getValidationResult={getValidationResult}
            inputHandler={inputHandler}
            setDirty={setDirty}
            maxCommentLength={MAX_COMMENT_LENGTH}
          />
        </div>

        {applicablePromos.length > 0 && (
          <div className={styles.Section}>
            <PromoSelector
              applicablePromos={applicablePromos}
              selectedPromo={selectedPromo}
              discountCode={discountCode}
              cartItems={itemsForOrder}
              onSelectPromo={handleSelectPromo}
              onDiscountCodeChange={setDiscountCode}
            />

            {selectedPromo?.giftBySum && giftItems.length > 0 && (
              <GiftSelector
                giftItems={giftItems}
                selectedGiftId={selectedGiftItemId}
                clusterId={clusterId}
                onSelect={setSelectedGiftItemId}
              />
            )}
          </div>
        )}

        {bonusBalance > 0 && (
          <div className={styles.Section}>
            <BonusInfo balance={bonusBalance} percent={bonusPercent} />

            {canSpendBonuses && (
              <BonusSpendSelector
                balance={bonusBalance}
                maxWriteOff={maxWriteOff}
                value={bonusesToSpend}
                onChange={setBonusesToSpend}
              />
            )}

            {bonusMode === "earn" && (
              <BonusEarnInfo earned={earnedBonuses} percent={bonusPercent} />
            )}

            <BonusMotivator text={nextLevelText} />
          </div>
        )}

        <div className={styles.Section}>
          <DeliveryTimeSelector
            displayText={deliveryTimeDisplay}
            pickerVisible={deliveryTimePickerVisible}
            minimumDate={deliveryTimeMinimumDate}
            onPress={openDeliveryTimePicker}
            onClose={closeDeliveryTimePicker}
            onSelect={onDeliveryTimeSelect}
            isTimeAvailable={isTimeAvailable}
          />
        </div>

        <div className={styles.Section}>
          <PaymentMethodSelector
            clusterId={clusterId}
            paymentMethod={paymentMethod}
            onSelect={handlePaymentMethodChange}
            deliveryType={deliveryType}
          />
        </div>

        {validationError && (
          <div className={styles.Section}>
            <ValidationError message={validationError} />
          </div>
        )}

        <div className={styles.Section}>
          <OrderSummary
            orderItems={itemsForOrder}
            remainingItems={itemsRemaining}
            subtotal={itemsTotal}
            deliveryFee={deliveryFee}
            discountAmount={discountAmount}
            bonusesSpent={bonusesToSpend}
            bonusesToAdd={bonusMode === "earn" ? earnedBonuses : 0}
            total={totalWithDelivery}
            getSaleItemName={(id) =>
              saleItemList.find((it) => it.id === id)?.name || `Товар #${id}`
            }
            hasPromo={!!selectedPromo}
            deliveryType={deliveryType}
            bestDeliveryText={null}
            storeName={storeList.find((s) => s.id === pickUpStore)?.name}
            deliveryAvailable={deliveryAvailable}
            deliveryError={deliveryError}
            selectedAddressId={selectedAddressId}
            deliveryService={deliveryService}
            cdekOptions={cdekOptions}
            cutleryCount={(cluster as any)?.cutlery ? cutleryCount : undefined}
            giftItemName={giftItemName}
          />
        </div>

        {(cluster as any)?.cutlery && (
          <div className={styles.Section}>
            <CutlerySelector
              value={cutleryCount}
              onIncrement={incrementCutlery}
              onDecrement={decrementCutlery}
            />
          </div>
        )}

        <div className={styles.Submit}>
          <Button
            title="Заказать"
            onClick={createOrder}
            variant="primary"
            disabled={orderDisabled}
            loading={isOrderCreating}
            fullWidth
          />
        </div>
      </div>
    </div>
  );
};
